import {
	MutableRefObject,
	useContext,
	FC,
	PropsWithChildren,
	useEffect,
	useState,
	useCallback,
} from 'react';
import { CircleArrowLeft, CircleArrowRight } from 'lucide-react';
import { AppContext } from '@/App.context';

export const Section: FC<PropsWithChildren<SectionProps>> = ({
	title,
	className,
	children,
	domNode,
}) => {
	return (
		<section className={`mt-1 mb-2 ${className}`}>
			<div className="flex justify-between p-4 pt-0 pb-2">
				<h3 className="pl-3 text-xl font-bold">{title}</h3>
				<NavigationBar domNode={domNode} />
			</div>
			{children}
		</section>
	);
};

interface SectionProps {
	title: string;
	className?: string;
	domNode: HTMLUListElement | null;
}

export function NavigationBar(props: NavigationBarProps) {
	const { device } = useContext(AppContext);
	const { domNode } = props;
	const isTouch = device?.isTouchable;
	const scrollAmount = props.scrollAmount || 500;

	const [isOverflowing, setIsOverflowing] = useState(false);

	useEffect(() => {
		if (!domNode) {
			return;
		}

		const overflowing = isContentOverflowing(domNode);

		setIsOverflowing(overflowing);
	}, [domNode, device]);

	return (
		<div
			className={`flex justify-center items-center space-x-1 ${(isTouch ||
				!isOverflowing) &&
				'hidden'}`}
		>
			<button
				className="cursor-pointer"
				onClick={() => {
					const node = domNode;
					if (!node) return;
					node.scrollTo({
						behavior: 'smooth',
						left: node.scrollLeft - scrollAmount,
					});
				}}
			>
				<CircleArrowLeft color={'#999'} />
			</button>
			<button
				className="cursor-pointer"
				onClick={() => {
					const node = domNode;
					if (!node) return;
					node.scrollTo({
						behavior: 'smooth',
						left: node.scrollLeft + scrollAmount,
					});
				}}
			>
				<CircleArrowRight color={'#999'} />
			</button>
		</div>
	);
}

function isContentOverflowing(element: HTMLUListElement | null) {
	if (!element) {
		return false;
	}
	// Check for horizontal overflow
	const horizontalOverflow = element.scrollWidth > element.clientWidth;

	// Check for vertical overflow
	const verticalOverflow = element.scrollHeight > element.clientHeight;

	return horizontalOverflow || verticalOverflow;
}

interface NavigationBarProps {
	domNode: HTMLUListElement | null;
	scrollAmount?: number;
}
